/**
 * ============================================================================
 * Eisstock & Laser Engraving Automated Transition Flow
 * www.holzeisstock.at | Eurus-Breeze + OPTIS + Simple Bundles
 * ============================================================================
 *
 * WORKFLOW & IMPLEMENTATION OVERVIEW:
 * ----------------------------------------------------------------------------
 * 1. Eisstock Product Page Configuration (Source):
 *    - Customer configures an Eisstock (e.g. body, handle, running surface).
 *    - In the OPTIS options form, customer chooses whether they want a Laser
 *      Engraving ("Ja/Yes" vs "Nein/No").
 *    - An automated random number of configurable length (e.g. 4 digits: 1000–9999)
 *      is generated, automatically populated into the Eisstock number textbox, and
 *      locked as read-only so customers cannot change it.
 *
 * 2. Add to Cart & Redirection:
 *    - When customer clicks "In den Warenkorb" / "Add to Cart":
 *      a) If Engraving is "No": Normal Eurus theme behavior (adds Eisstock to cart,
 *         shows notification / opens drawer, no redirection).
 *      b) If Engraving is "Yes":
 *         - Captures the generated random engraving number from the textbox or
 *           cart line item properties.
 *         - Stores the number in sessionStorage, localStorage, and cookie, and
 *           appends it to URL query parameters.
 *         - Waits for the asynchronous Add-to-Cart request to succeed (ensuring
 *           Simple Bundles & Eisstock are fully added to cart first).
 *         - Displays a visual countdown banner / notification informing the customer
 *           that they are being forwarded to the laser engraving configuration.
 *         - After a 3-second delay, redirects automatically to the Laser Engraving
 *           product page (/products/lasergravur or /en/products/lasergravur).
 *
 * 3. Laser Engraving Product Page (Target: /products/lasergravur):
 *    - Reads the passed random engraving number from URL query parameter, sessionStorage,
 *      localStorage, or cookie.
 *    - Uses a MutationObserver and stepped retry intervals to watch for OPTIS asynchronous
 *      rendering.
 *    - Automatically pre-fills the matching Eisstock number textbox with the
 *      transferred random number across both German and English locales, and locks
 *      it as read-only to prevent customer edits.
 *    - Dispatches input/change events to ensure OPTIS calculations and validation
 *      stay fully synchronized.
 *    - Customer chooses font, inputs engraving text, and adds the engraving to cart.
 *
 * 4. Downstream Processing:
 *    - Both the configured Eisstock and the Laser Engraving exist as paired items
 *      with matching random identifier numbers in Shopify's cart, ready for
 *      Billbee order fulfillment.
 * ============================================================================
 */
(function() {
    'use strict';

    const CONFIG = {
        engravingPathHandle: 'lasergravur',
        storageKey: 'holzeisstock_selected_engraving_no',
        queryParam: 'engraving_number',
        redirectDelayMs: 3000, // 3s delay allowing customer to clearly see the slide-in cart notification
        randomNumberLength: 4, // Configurable length of the generated random engraving number (e.g. 4 -> 1000–9999)
        knownNumberOptionIds: ['1647332', '1638356'] // Exact option IDs for engraving number fields in OPTIS v2
    };

    /**
     * Generates a random numeric string of specified length (e.g. length 4 -> "1000" to "9999")
     */
    function generateRandomEngravingNumber(length = CONFIG.randomNumberLength) {
        const len = Math.max(1, parseInt(length, 10) || 4);
        const min = Math.pow(10, len - 1);
        const max = Math.pow(10, len) - 1;
        return String(Math.floor(min + Math.random() * (max - min + 1)));
    }

    /**
     * Persistently saves the transferred engraving number
     */
    function saveEngravingNumber(num) {
        if (!num) return;
        try {
            sessionStorage.setItem(CONFIG.storageKey, num);
            localStorage.setItem(CONFIG.storageKey, num);
        } catch (e) {}
        try {
            document.cookie = `${CONFIG.storageKey}=${encodeURIComponent(num)}; path=/; max-age=3600; SameSite=Lax`;
        } catch (e) {}
    }

    /**
     * Retrieves the transferred engraving number from URL params, session storage, local storage, or cookie
     */
    function getTransferredNumber() {
        const urlParams = new URLSearchParams(window.location.search);
        let num = urlParams.get(CONFIG.queryParam) ||
                  urlParams.get('engraving_number') ||
                  urlParams.get('number') ||
                  urlParams.get('no') ||
                  urlParams.get('eisstock') ||
                  urlParams.get('id');

        if (!num) {
            try {
                num = sessionStorage.getItem(CONFIG.storageKey) || localStorage.getItem(CONFIG.storageKey);
            } catch (e) {}
        }

        if (!num) {
            try {
                const cookieMatch = document.cookie.match(new RegExp('(?:^|; )' + CONFIG.storageKey + '=([^;]*)'));
                if (cookieMatch) {
                    num = decodeURIComponent(cookieMatch[1]);
                }
            } catch (e) {}
        }

        return num ? String(num).trim() : null;
    }

    /**
     * Determines if current URL is the Laser Engraving product page (in any language)
     */
    function isEngravingProductPage() {
        return window.location.pathname.includes('/products/' + CONFIG.engravingPathHandle);
    }

    /**
     * Detects if current page is in English
     */
    function isEnglishLocale() {
        const path = window.location.pathname.toLowerCase();
        const htmlLang = (document.documentElement.lang || '').toLowerCase();
        return path.startsWith('/en/') || path === '/en' || htmlLang.startsWith('en');
    }

    /**
     * Builds the localized destination URL for the Laser Engraving product
     */
    function getEngravingTargetUrl(engravingNumber) {
        let root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        
        // Detect locale prefix in URL path if Shopify.routes.root is not localized (e.g. /en/)
        if (root === '/' || !root) {
            const match = window.location.pathname.match(/^(\/[a-zA-Z]{2}(?:-[a-zA-Z]{2})?)\//);
            if (match) {
                root = match[1] + '/';
            }
        }
        
        const cleanRoot = root.endsWith('/') ? root : `${root}/`;
        const basePath = `${cleanRoot}products/${CONFIG.engravingPathHandle}`;
        const targetUrl = new URL(basePath, window.location.origin);
        
        if (engravingNumber) {
            targetUrl.searchParams.set(CONFIG.queryParam, engravingNumber);
        }
        return targetUrl.toString();
    }

    /**
     * Checks if a string or option value represents a positive "Yes" choice
     */
    function isPositiveEngravingChoice(val, text) {
        const str = `${val || ''} ${text || ''}`.toLowerCase().trim();
        if (!str) return false;
        
        // Negative indicators
        if (/\b(nein|no|ohne|keine|none|without|false|0)\b/i.test(str)) {
            return false;
        }
        
        // Positive indicators
        if (/\b(ja|yes|mit|with|true|1)\b/i.test(str) || str.startsWith('ja') || str.startsWith('yes')) {
            return true;
        }
        return false;
    }

    /**
     * Extracts an engraving number from an option value or text string (excluding price tags)
     */
    function extractNumberFromValue(val, text) {
        const str = `${val || ''} ${text || ''}`.trim();
        if (!str) return null;
        // Remove currency/prices (e.g. + € 12,00 or 12.00 €) to prevent false numerical matches
        const cleanStr = str.replace(/[+–-]?\s*€\s*\d+([.,]\d+)?|[+–-]?\s*\d+([.,]\d+)?\s*€/g, '');
        const match = cleanStr.match(/\b\d+\b/) || cleanStr.match(/\d+/);
        return match ? match[0] : null;
    }

    /**
     * Resolves the engraving number input directly via known OPTIS v2 IDs and data attributes
     */
    function getDirectEngravingNumberInput(root = document) {
        if (!root) return null;
        const selector = [
            '#optis-po-product-1-control-1647332',
            '#optis-po-product-1-control-1638356',
            'input[id*="control-1647332"]',
            'input[id*="control-1638356"]',
            '[data-optis-po-option-id="1647332"] input',
            '[data-optis-po-option-id="1638356"] input',
            '[data-optis-po-option-id="1647332"] .optisPo-textOption__input',
            '[data-optis-po-option-id="1638356"] .optisPo-textOption__input'
        ].join(', ');
        try {
            const el = root.querySelector(selector);
            if (el && !isExcludedInput(el)) return el;
            return null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Checks if an input is an excluded control (e.g. live preview size inputs, preview adjustments, system fields)
     */
    function isExcludedInput(input) {
        if (!input) return true;
        if (input.type === 'hidden' || input.type === 'submit' || input.type === 'button') return true;

        const className = (input.className || '').toLowerCase();
        const id = (input.id || '').toLowerCase();
        const name = (input.name || '').toLowerCase();

        // Live preview elements (e.g. optisPo-livePreview__sizeInput, zoom, font size sliders)
        if (className.includes('livepreview') || className.includes('live-preview') || className.includes('preview') || className.includes('sizeinput')) {
            return true;
        }
        if (id.includes('preview') || id.includes('sizeinput') || name.includes('preview') || name.includes('sizeinput')) {
            return true;
        }
        if (input.closest('.optisPo-livePreview, [class*="livePreview"], [class*="live-preview"], .optisPo-preview, [class*="preview-toolbar"], [class*="previewToolbar"]')) {
            return true;
        }

        // System / Cart / Search inputs
        if (name === 'quantity' || name === 'q' || name === 'id' || name === 'utf8' || name === 'form_type' || name.includes('search')) {
            return true;
        }
        if (className.includes('quantity') || className.includes('qty')) {
            return true;
        }

        return false;
    }

    /**
     * Gathers all human-readable label / title text associated with an option container or input
     */
    function getFieldLabelText(container, input) {
        let text = '';
        if (input) {
            if (input.id) {
                text += ' ' + input.id;
                const directLabel = document.querySelector(`label[for="${input.id}"]`);
                if (directLabel && directLabel.textContent) {
                    text += ' ' + directLabel.textContent;
                }
            }
            text += ' ' + (input.getAttribute('aria-label') || '');
            text += ' ' + (input.getAttribute('data-name') || '');
            text += ' ' + (input.getAttribute('data-optis-po-option-id') || '');
            text += ' ' + (input.className || '');
            text += ' ' + (input.placeholder || '');
            const rawName = (input.name || '').replace(/properties\[_?bss_po_option_(?:text_)?/i, '').replace(/[\[\]_]/g, ' ');
            text += ' ' + rawName;
        }
        if (container) {
            const optId = container.getAttribute('data-optis-po-option-id') || container.getAttribute('data-option-id') || '';
            if (optId) text += ' ' + optId;
            const labelEl = container.querySelector('label, .optisPo-option__label, .optisPo-visuallyHidden, .optisPo-help, .bss-po-title, .optis-title, .po-title, legend, .form__label, [class*="title"], [class*="label"], [class*="header"]');
            if (labelEl && labelEl.textContent) {
                text += ' ' + labelEl.textContent;
            }
        }
        if (container && !text.trim()) {
            text = container.textContent || '';
        }
        return text.trim().toLowerCase();
    }

    /**
     * Checks if a field label specifically represents font styling, live preview controls, or custom engraving text (to never overwrite)
     */
    function isCustomTextOrFont(label) {
        const l = (label || '').toLowerCase();
        // Live preview / size inputs
        if (/preview|livepreview|live-preview|sizeinput|size-input|fontsize|font-size|schriftgr|zeichengr|groesse|größe/i.test(l)) return true;
        // Font selection
        if (/schrift|font|style|design|typograph/i.test(l)) return true;
        // Engraving text message / lines
        if (/kurz|lang|short|long|zeile|line|nachricht|message|wunschtext|gravurtext|gravur\s*text|custom\s*text|your\s*text/i.test(l)) return true;
        // Standalone text options without number or stock assignment context
        if (/\btext\b/i.test(l) && !/nummer|number|\bno\.?|\b#\b|zuordnung|kennzeichnung/i.test(l)) return true;
        return false;
    }

    /**
     * Checks if a field label represents the Eisstock / Engraving Number field
     */
    function isEisstockNumberField(label) {
        const l = (label || '').toLowerCase();
        if (isCustomTextOrFont(l)) return false;
        if (CONFIG.knownNumberOptionIds && CONFIG.knownNumberOptionIds.some(id => l.includes(id))) return true;
        return /nummer|number|\bno\.?\b|\bnr\.?\b|\b#\b|gravurnummer|gravur-nummer|engraving-number|engraving\s*number|zuordnung|kennzeichnung|zugehörigkeit|zugehoerigkeit|identifier|(?:eisstock|ice\s*stock|stock)\s*[-_ ]*(?:nr|no|num|nummer|number|id|code|kennzeichnung|zuordnung)/i.test(l);
    }

    /**
     * Finds text / number input in a container
     */
    function findNumberInputInContainer(container) {
        if (!container) return null;
        if (container.closest('.optisPo-livePreview, [class*="livePreview"], [class*="live-preview"]')) return null;
        const direct = getDirectEngravingNumberInput(container);
        if (direct && !isExcludedInput(direct)) return direct;
        const inputs = container.querySelectorAll('.optisPo-textOption__input, .optisPo-numberOption__input, input[type="text"], input[type="number"], input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="submit"]):not([type="button"])');
        for (const input of inputs) {
            if (isExcludedInput(input)) continue;
            const labelText = getFieldLabelText(container, input);
            if (!isCustomTextOrFont(labelText) && isEisstockNumberField(labelText)) {
                return input;
            }
        }
        return null;
    }

    /**
     * Locks the engraving number input so customers cannot modify the generated or transferred number,
     * while keeping it read-only (instead of disabled) so it reliably submits with the Shopify product form / FormData.
     */
    function lockNumberInput(input) {
        if (!input) return;
        input.readOnly = true;
        input.setAttribute('readonly', 'readonly');
        input.setAttribute('tabindex', '-1');
        input.style.cursor = 'not-allowed';
        input.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
        input.style.opacity = '0.9';
        input.style.userSelect = 'none';

        if (!input.__holzeisstockLocked) {
            input.__holzeisstockLocked = true;
            input.addEventListener('keydown', function(e) {
                if (e.key !== 'Tab') {
                    e.preventDefault();
                }
            });
            input.addEventListener('paste', function(e) {
                e.preventDefault();
            });
            input.addEventListener('cut', function(e) {
                e.preventDefault();
            });
            input.addEventListener('drop', function(e) {
                e.preventDefault();
            });
        }
    }

    /**
     * Sets input value and dispatches change/input events across all JavaScript frameworks (Alpine, React, Vue, jQuery)
     */
    function setInputValueAndDispatch(input, value) {
        if (!input || value === undefined || value === null) return;
        const strVal = String(value).trim();
        if (!strVal) return;

        input.value = strVal;
        input.setAttribute('value', strVal);

        // Modern framework setter dispatch
        try {
            const prototype = Object.getPrototypeOf(input);
            const valueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set ||
                                Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
            if (valueSetter) {
                valueSetter.call(input, strVal);
            }
        } catch (e) {}

        input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        input.dispatchEvent(new Event('blur', { bubbles: true, cancelable: true }));

        lockNumberInput(input);
    }

    /**
     * Inspects product properties payload returned by Shopify cart add response
     */
    function getSelectionFromProperties(props) {
        if (!props || typeof props !== 'object') return { wantsEngraving: false, engravingNumber: null };
        let wantsEngraving = false;
        let engravingNumber = null;

        for (const [key, rawVal] of Object.entries(props)) {
            const k = key.toLowerCase();
            const v = String(rawVal || '').trim();

            if (/gravur|engrav|laser/i.test(k)) {
                if (isPositiveEngravingChoice(v, '')) {
                    wantsEngraving = true;
                }
            }
            if (/nummer|number|\bno\.?|\b#\b|zuordnung|eisstock|ice\s*stock|\bstock\b|\bid\b/i.test(k) && !isCustomTextOrFont(k)) {
                const num = extractNumberFromValue(v, '');
                if (num) {
                    engravingNumber = num;
                }
            }
        }
        return { wantsEngraving, engravingNumber };
    }

    /**
     * Inspects current DOM inputs, dropdowns, and swatches for engraving selections
     */
    function getSelectionFromDOM() {
        let wantsEngraving = false;
        let engravingNumber = null;

        const optionContainers = document.querySelectorAll('.optisPo-option, [data-optis-po-option-id], .optisPo-optionSet__cell, .bss-po-option, .optis-option, .po-option, .product-form__input, fieldset');

        optionContainers.forEach(container => {
            if (container.closest('.optisPo-livePreview, [class*="livePreview"], [class*="live-preview"]')) return;
            const labelText = getFieldLabelText(container);
            const containerText = (container.textContent || '').trim().toLowerCase();

            const isEngravingQuestion = /gravur|engrav|laser/i.test(labelText) || /gravur|engrav/i.test(containerText);
            const isNumberField = isEisstockNumberField(labelText);

            // 1. Text / Number Inputs in this container
            const textInput = findNumberInputInContainer(container);
            if (textInput && isNumberField && !isExcludedInput(textInput)) {
                const val = (textInput.value || '').trim();
                if (val) {
                    const num = extractNumberFromValue(val, '');
                    if (num) engravingNumber = num;
                }
            }

            // 2. Radio / Checkboxes in this container
            const inputs = container.querySelectorAll('input');
            inputs.forEach(input => {
                if (isExcludedInput(input)) return;
                const val = (input.value || '').trim();
                const parentLabel = input.closest('label')?.textContent || '';
                if ((input.type === 'radio' || input.type === 'checkbox') && input.checked) {
                    if (isEngravingQuestion && isPositiveEngravingChoice(val, parentLabel)) {
                        wantsEngraving = true;
                    }
                    if (isNumberField) {
                        const num = extractNumberFromValue(val, parentLabel);
                        if (num) engravingNumber = num;
                    }
                } else if (input.type === 'text' || input.type === 'number') {
                    if (val) {
                        if (isEngravingQuestion && isPositiveEngravingChoice(val, parentLabel)) {
                            wantsEngraving = true;
                        }
                        if (isNumberField && !engravingNumber) {
                            const num = extractNumberFromValue(val, parentLabel);
                            if (num) engravingNumber = num;
                        }
                    }
                }
            });

            // 3. Selects in this container
            const selects = container.querySelectorAll('select');
            selects.forEach(select => {
                const selectedOption = select.options[select.selectedIndex];
                if (selectedOption) {
                    const val = (selectedOption.value || '').trim();
                    const text = (selectedOption.text || '').trim();
                    if (isEngravingQuestion && isPositiveEngravingChoice(val, text)) {
                        wantsEngraving = true;
                    }
                    if (isNumberField) {
                        const num = extractNumberFromValue(val, text);
                        if (num) engravingNumber = num;
                    }
                }
            });

            // 4. Custom dropdowns / swatches / buttons in this container
            const activeCustom = container.querySelectorAll('.bss-po-selected, .po-selected, .active[data-value], [aria-checked="true"], .po-dropdown-option.selected');
            activeCustom.forEach(el => {
                const val = el.getAttribute('data-value') || el.textContent || '';
                if (isEngravingQuestion && isPositiveEngravingChoice(val, el.textContent)) {
                    wantsEngraving = true;
                }
                if (isNumberField) {
                    const num = extractNumberFromValue(val, el.textContent);
                    if (num) engravingNumber = num;
                }
            });
        });

        // Fallback: check all checked radios / selected options / inputs across the document if not found
        if (!wantsEngraving || !engravingNumber) {
            const allChecked = document.querySelectorAll('input:checked, select');
            allChecked.forEach(el => {
                if (el.tagName === 'INPUT') {
                    if (isExcludedInput(el)) return;
                    const val = (el.value || '').trim();
                    const labelText = el.closest('label')?.textContent || el.parentElement?.textContent || '';
                    const name = (el.name || '').toLowerCase();
                    if (!wantsEngraving && (/gravur|engrav/i.test(name) || /gravur|engrav/i.test(labelText))) {
                        if (isPositiveEngravingChoice(val, labelText)) wantsEngraving = true;
                    }
                    if (!engravingNumber && isEisstockNumberField(name + ' ' + labelText)) {
                        const num = extractNumberFromValue(val, labelText);
                        if (num) engravingNumber = num;
                    }
                } else if (el.tagName === 'SELECT') {
                    const opt = el.options[el.selectedIndex];
                    if (opt) {
                        const val = opt.value || '';
                        const text = opt.text || '';
                        const name = (el.name || '').toLowerCase();
                        if (!wantsEngraving && (/gravur|engrav/i.test(name) || /gravur|engrav/i.test(text))) {
                            if (isPositiveEngravingChoice(val, text)) wantsEngraving = true;
                        }
                        if (!engravingNumber && isEisstockNumberField(name + ' ' + text)) {
                            const num = extractNumberFromValue(val, text);
                            if (num) engravingNumber = num;
                        }
                    }
                }
            });
        }

        return { wantsEngraving, engravingNumber };
    }

    /**
     * Injects a visual countdown / waiting notification banner during redirection delay
     */
    function showRedirectionWaitingBanner(durationMs = 3000) {
        if (document.getElementById('holzeisstock-redirect-banner')) return;

        const isEnglish = isEnglishLocale();
        const titleText = isEnglish
            ? 'Ice stock added to cart !'
            : 'Eisstock wurde zum Warenkorb hinzugefügt !';
        const subtitleText = isEnglish
            ? 'Forwarding to Step 2 - Laser Engraving ...'
            : 'Weiterleitung zu Schritt2 - Lasergravur ...';

        const banner = document.createElement('div');
        banner.id = 'holzeisstock-redirect-banner';
        banner.setAttribute('role', 'status');
        banner.setAttribute('aria-live', 'polite');

        banner.innerHTML = `
            <style>
                #holzeisstock-redirect-banner {
                    position: fixed;
                    top: 24px;
                    left: 50%;
                    transform: translateX(-50%) translateY(-20px);
                    z-index: 2147483647;
                    background: #683B11;
                    color: #F7F2EC;
                    border: 1px solid rgba(177, 127, 73, 0.4);
                    border-radius: 12px;
                    padding: 16px 22px;
                    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.2);
                    display: flex;
                    flex-direction: column;
                    min-width: 320px;
                    max-width: 90vw;
                    font-family: inherit;
                    opacity: 0;
                    transition: opacity 0.3s ease, transform 0.3s ease;
                    pointer-events: none;
                }
                #holzeisstock-redirect-banner.is-visible {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                }
                .holzeisstock-banner-content {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .holzeisstock-spinner-ring {
                    width: 22px;
                    height: 22px;
                    border: 2.5px solid rgba(255, 255, 255, 0.25);
                    border-top-color: #B17F49;
                    border-radius: 50%;
                    flex-shrink: 0;
                    animation: holzeisstock-spin 0.8s linear infinite;
                }
                .holzeisstock-banner-text {
                    flex: 1;
                }
                .holzeisstock-banner-title {
                    font-size: 18px;
                    font-weight: 600;
                    line-height: 1.3;
                    margin-bottom: 2px;
                    color: #F7F2EC;
                }
                .holzeisstock-banner-subtitle {
                    font-size: 16px;
                    color: rgba(247, 242, 236, 0.82);
                    line-height: 1.3;
                }
                .holzeisstock-progress-track {
                    margin-top: 12px;
                    width: 100%;
                    height: 4px;
                    background: rgba(255, 255, 255, 0.15);
                    border-radius: 999px;
                    overflow: hidden;
                }
                .holzeisstock-progress-bar {
                    width: 0%;
                    height: 100%;
                    background: linear-gradient(90deg, #B17F49, #D9C1A5);
                    border-radius: 999px;
                    transition: width ${durationMs}ms linear;
                }
                @keyframes holzeisstock-spin {
                    to { transform: rotate(360deg); }
                }
                @media (max-width: 767px) {
                    #holzeisstock-redirect-banner {
                        top: auto;
                        bottom: 20px;
                        transform: translateX(-50%) translateY(20px);
                    }
                    #holzeisstock-redirect-banner.is-visible {
                        transform: translateX(-50%) translateY(0);
                    }
                }
            </style>
            <div class="holzeisstock-banner-content">
                <div class="holzeisstock-spinner-ring" aria-hidden="true"></div>
                <div class="holzeisstock-banner-text">
                    <div class="holzeisstock-banner-title">${titleText}</div>
                    <div class="holzeisstock-banner-subtitle">${subtitleText}</div>
                </div>
            </div>
            <div class="holzeisstock-progress-track" aria-hidden="true">
                <div class="holzeisstock-progress-bar" id="holzeisstock-redirect-progress"></div>
            </div>
        `;

        document.body.appendChild(banner);

        // Animate entrance and progress bar
        requestAnimationFrame(() => {
            banner.classList.add('is-visible');
            const bar = document.getElementById('holzeisstock-redirect-progress');
            if (bar) {
                requestAnimationFrame(() => {
                    bar.style.width = '100%';
                });
            }
        });
    }

    /**
     * Handler for Configurable Eisstock Pages
     */
    function initEisstockPage() {
        let generatedRandomNumber = generateRandomEngravingNumber(CONFIG.randomNumberLength);
        let cachedSelection = { wantsEngraving: false, engravingNumber: generatedRandomNumber };
        let redirectTimer = null;

        /**
         * Ensures the Eisstock number textbox is populated with our generated random number
         */
        function populateEisstockNumberTextbox() {
            // First check direct known input element
            const directInput = getDirectEngravingNumberInput(document);
            if (directInput && !isExcludedInput(directInput)) {
                const currentVal = (directInput.value || '').trim();
                if (!currentVal) {
                    setInputValueAndDispatch(directInput, generatedRandomNumber);
                } else {
                    generatedRandomNumber = currentVal;
                }
                lockNumberInput(directInput);
                return;
            }

            const optionContainers = document.querySelectorAll('.optisPo-option, [data-optis-po-option-id], .optisPo-optionSet__cell, .bss-po-option, .optis-option, .po-option, .product-form__input, fieldset');
            let found = false;

            optionContainers.forEach(container => {
                if (container.closest('.optisPo-livePreview, [class*="livePreview"], [class*="live-preview"]')) return;
                const labelText = getFieldLabelText(container);
                if (isEisstockNumberField(labelText)) {
                    const input = findNumberInputInContainer(container);
                    if (input && !isExcludedInput(input)) {
                        found = true;
                        const currentVal = (input.value || '').trim();
                        if (!currentVal) {
                            setInputValueAndDispatch(input, generatedRandomNumber);
                        } else {
                            generatedRandomNumber = currentVal;
                        }
                        lockNumberInput(input);
                    }
                }
            });

            // Global fallback for number input
            if (!found) {
                const allInputs = document.querySelectorAll('.optisPo-textOption__input, .optisPo-numberOption__input, [id*="control-1638356"], [id*="control-1647332"], form[action*="/cart/add"] input[type="text"], form[action*="/cart/add"] input[type="number"], .product-form input[type="text"], .product-form input[type="number"], input[name^="properties"]');
                for (const input of allInputs) {
                    if (isExcludedInput(input)) continue;
                    const labelText = getFieldLabelText(input.closest('.optisPo-option, [data-optis-po-option-id], .optisPo-optionSet__cell, .bss-po-option, .optis-option, .po-option, fieldset, div'), input);
                    if (isEisstockNumberField(labelText)) {
                        const currentVal = (input.value || '').trim();
                        if (!currentVal) {
                            setInputValueAndDispatch(input, generatedRandomNumber);
                        } else {
                            generatedRandomNumber = currentVal;
                        }
                        lockNumberInput(input);
                        break;
                    }
                }
            }
        }

        function updateCachedSelection() {
            populateEisstockNumberTextbox();
            const current = getSelectionFromDOM();
            if (current.wantsEngraving) {
                cachedSelection = current;
                if (!cachedSelection.engravingNumber) {
                    cachedSelection.engravingNumber = generatedRandomNumber;
                }
            }
        }

        // Continually track changes in form options and keep textbox populated
        document.addEventListener('change', updateCachedSelection, true);
        document.addEventListener('click', updateCachedSelection, true);
        document.addEventListener('input', function(e) {
            const input = e.target;
            if (input && (input.type === 'text' || input.type === 'number')) {
                const labelText = getFieldLabelText(input.closest('.bss-po-option, .optis-option, .po-option, fieldset, div'), input);
                if (isEisstockNumberField(labelText)) {
                    const val = (input.value || '').trim();
                    if (val) {
                        generatedRandomNumber = val;
                        cachedSelection.engravingNumber = val;
                    }
                }
            }
        }, true);

        // Pre-emptively record state before Add to Cart submits
        document.addEventListener('click', function(e) {
            const atcButton = e.target.closest('[name="add"], .product-form__submit, button[type="submit"], [data-add-to-cart]');
            if (atcButton) {
                populateEisstockNumberTextbox();
                updateCachedSelection();
            }
        }, true);

        // Observe dynamic OPTIS rendering on Eisstock page to auto-fill textbox immediately
        populateEisstockNumberTextbox();
        [50, 150, 300, 600, 1000, 1800, 3000].forEach(delay => {
            setTimeout(populateEisstockNumberTextbox, delay);
        });

        const observer = new MutationObserver(() => {
            populateEisstockNumberTextbox();
        });
        observer.observe(document.body, { childList: true, subtree: true });

        // Handle successful cart addition dispatched by Eurus theme
        document.addEventListener('eurus:product:added', function(e) {
            if (redirectTimer) return; // Prevent duplicate redirection triggers

            const payload = e && e.detail && e.detail.product;
            const props = (payload && payload.properties) || {};

            const fromProps = getSelectionFromProperties(props);
            const fromDOM = getSelectionFromDOM();

            const wantsEngraving = fromProps.wantsEngraving || fromDOM.wantsEngraving || cachedSelection.wantsEngraving;
            const engravingNumber = fromProps.engravingNumber || fromDOM.engravingNumber || cachedSelection.engravingNumber || generatedRandomNumber;

            if (wantsEngraving) {
                saveEngravingNumber(engravingNumber);

                const targetUrl = getEngravingTargetUrl(engravingNumber);

                // Display visual waiting / transition progress sign
                showRedirectionWaitingBanner(CONFIG.redirectDelayMs);

                // Give customer time to view the notification before navigating
                redirectTimer = setTimeout(function() {
                    window.location.href = targetUrl;
                }, CONFIG.redirectDelayMs);
            }
        });
    }

    /**
     * Helper to cleanly close an OPTIS dropdown container without affecting other components (if present)
     */
    function closeOptisDropdown(container) {
        if (!container) return;
        const dropdownBox = container.closest('[id^="bss-po_dropdown"], [id^="po_dropdown_thumbnail-div"], .po-option--type-dropdown-menu, .po-option--dropdown-wrapper') || container;
        dropdownBox.classList.remove('open-drop-list');

        const lists = dropdownBox.querySelectorAll('.po-dropdown-list, .po-dropdown-thumbnail-list');
        lists.forEach(list => {
            list.style.display = 'none';
        });
    }

    /**
     * Handler for Laser Engraving Product Page (/products/lasergravur or /en/products/lasergravur)
     */
    function initEngravingPage() {
        const targetNumber = getTransferredNumber();
        if (!targetNumber) return;

        let applied = false;
        let customerEdited = false;

        // Track if customer intentionally edits the field manually
        document.addEventListener('input', function(e) {
            if (e.isTrusted && e.target) {
                const labelText = getFieldLabelText(e.target.closest('.bss-po-option, .optis-option, .po-option, fieldset, div'), e.target);
                if (isEisstockNumberField(labelText)) {
                    customerEdited = true;
                }
            }
        }, true);

        function applyPreselection() {
            if (customerEdited) return;

            // Direct check for known inputs on engraving page
            const directInput = getDirectEngravingNumberInput(document);
            if (directInput && !isExcludedInput(directInput)) {
                if (directInput.value !== targetNumber) {
                    setInputValueAndDispatch(directInput, targetNumber);
                } else {
                    lockNumberInput(directInput);
                }
                applied = true;
                return;
            }

            // Method 1: Search OPTIS option containers
            const optionContainers = document.querySelectorAll('.optisPo-option, [data-optis-po-option-id], .optisPo-optionSet__cell, .bss-po-option, .optis-option, .po-option, .product-form__input, [id^="bss-po_dropdown"], [id^="po_dropdown_thumbnail-div"], .po-option--type-dropdown-menu, fieldset');

            optionContainers.forEach(container => {
                if (container.closest('.optisPo-livePreview, [class*="livePreview"], [class*="live-preview"]')) return;
                const labelText = getFieldLabelText(container);
                if (isCustomTextOrFont(labelText)) return;

                if (isEisstockNumberField(labelText)) {
                    // 1. Text input inside number container (Primary)
                    const numberInput = findNumberInputInContainer(container);
                    if (numberInput && !isExcludedInput(numberInput)) {
                        if (numberInput.value !== targetNumber) {
                            setInputValueAndDispatch(numberInput, targetNumber);
                        } else {
                            lockNumberInput(numberInput);
                        }
                        applied = true;
                        return;
                    }

                    // 2. Custom OPTIS dropdown fallback
                    const dropdownOptions = container.querySelectorAll('.po-dropdown-option, .po-dropdown-thumbnail-option');
                    if (dropdownOptions.length > 0) {
                        let matchesTargetOpt = null;
                        dropdownOptions.forEach(opt => {
                            if (opt.id === 'searchWithDropdown' || opt.id === 'searchWithThumbnail') return;
                            const dataVal = opt.getAttribute('data-value') || '';
                            const spanText = opt.querySelector('span')?.textContent || opt.textContent || '';
                            const num = extractNumberFromValue(dataVal, spanText);
                            
                            if (dataVal.trim() === targetNumber || spanText.trim() === targetNumber || num === targetNumber) {
                                matchesTargetOpt = { opt, dataVal, spanText };
                            }
                        });

                        if (matchesTargetOpt) {
                            const { opt, spanText } = matchesTargetOpt;
                            const trigger = container.querySelector('#bss-po-dropdown-selected-option, #po-dropdown-selected-option, .po-dropdown-selected-option');

                            dropdownOptions.forEach(item => item.classList.remove('selected'));
                            opt.classList.add('selected');

                            if (trigger) {
                                const spanHtml = opt.querySelector('span')?.innerHTML || spanText.trim();
                                trigger.innerHTML = `${spanHtml} <i class="po-dropdown-arrow po-dropdown-arrow-down"></i>`;
                            }

                            const selectEl = container.querySelector('select') || document.querySelector('select[id^="po_dropdown"]');
                            if (selectEl) {
                                for (let i = 0; i < selectEl.options.length; i++) {
                                    const selectOpt = selectEl.options[i];
                                    const numInOpt = extractNumberFromValue(selectOpt.value, selectOpt.text);
                                    if (numInOpt === targetNumber || selectOpt.value.trim() === targetNumber || selectOpt.text.trim() === targetNumber) {
                                        selectOpt.selected = true;
                                        selectOpt.setAttribute('selected', 'true');
                                        selectEl.selectedIndex = i;
                                        selectEl.value = selectOpt.value;
                                    } else {
                                        selectOpt.selected = false;
                                        selectOpt.removeAttribute('selected');
                                    }
                                }
                                selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                                selectEl.dispatchEvent(new Event('input', { bubbles: true }));
                            }

                            closeOptisDropdown(container);
                            setTimeout(() => closeOptisDropdown(container), 50);
                            setTimeout(() => closeOptisDropdown(container), 200);

                            applied = true;
                            return;
                        }
                    }

                    // 3. Standard Select fallback
                    const nativeSelect = container.querySelector('select');
                    if (nativeSelect) {
                        for (let i = 0; i < nativeSelect.options.length; i++) {
                            const opt = nativeSelect.options[i];
                            const optNum = extractNumberFromValue(opt.value, opt.text);
                            if (optNum === targetNumber || opt.value.trim() === targetNumber || opt.text.trim() === targetNumber) {
                                nativeSelect.selectedIndex = i;
                                nativeSelect.value = opt.value;
                                opt.selected = true;
                                opt.setAttribute('selected', 'true');
                                nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
                                nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
                                applied = true;
                                return;
                            }
                        }
                    }

                    // 4. Radio buttons fallback
                    const radios = container.querySelectorAll('input[type="radio"]');
                    radios.forEach(radio => {
                        const rLabel = radio.closest('label')?.textContent || '';
                        const radioNum = extractNumberFromValue(radio.value, rLabel);
                        if (radioNum === targetNumber || radio.value.trim() === targetNumber) {
                            radio.checked = true;
                            radio.dispatchEvent(new Event('change', { bubbles: true }));
                            radio.dispatchEvent(new Event('input', { bubbles: true }));
                            applied = true;
                        }
                    });
                }
            });

            // Method 2: Global text inputs search
            if (!applied) {
                const allInputs = document.querySelectorAll('.optisPo-textOption__input, .optisPo-numberOption__input, [id*="control-1647332"], [id*="control-1638356"], form[action*="/cart/add"] input[type="text"], form[action*="/cart/add"] input[type="number"], .product-form input[type="text"], .product-form input[type="number"], input[name^="properties"]');
                for (const input of allInputs) {
                    if (isExcludedInput(input)) continue;

                    const labelText = getFieldLabelText(input.closest('.optisPo-option, [data-optis-po-option-id], .optisPo-optionSet__cell, .bss-po-option, .optis-option, .po-option, fieldset, div'), input);
                    if (isCustomTextOrFont(labelText)) continue;

                    if (isEisstockNumberField(labelText)) {
                        if (input.value !== targetNumber) {
                            setInputValueAndDispatch(input, targetNumber);
                        } else {
                            lockNumberInput(input);
                        }
                        applied = true;
                        break;
                    }
                }
            }
        }

        // Attempt immediate preselection and retry across asynchronous DOM rendering cycles
        applyPreselection();

        [50, 150, 300, 600, 1000, 1500, 2200, 3200, 4500, 6000].forEach(delay => {
            setTimeout(applyPreselection, delay);
        });

        const observer = new MutationObserver(() => {
            applyPreselection();
        });

        observer.observe(document.body, { childList: true, subtree: true });

        setTimeout(() => {
            observer.disconnect();
        }, 10000);
    }

    // Initialize upon DOM readiness
    function init() {
        if (isEngravingProductPage()) {
            initEngravingPage();
        } else {
            initEisstockPage();
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();